import React, { useContext, useEffect, useState } from 'react';
import { Grid2, MenuItem } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useFlags } from 'launchdarkly-react-client-sdk';
import ExpenseTextField from './ExpenseTextField';
import { billTypes } from '../../Configurations/ExpenseEntryConfig';
import { ExpenseStatusValuesEnum } from './ExpenseStatus.enum';
import { ValidationModeContext } from '../../../contexts/VendorPaymentValidationModeContext';
import ValidationModeEnum from './ValidationMode.enum';
import { ExpenseModeEnum } from './ExpenseMode.enum';
import { ExpenseClassEnum } from './ExpenseClass.enum';
import { BillTypeEnum } from './BillType.enum';
import { ExpenseCategoryEnum } from './ExpenseCategory.enum';

const ExpenseBillType = (props) => {
    const {
        index,
        expense,
        expenseClass,
        expenseCategory,
        isClinicianReimbursement,
        mode,
        isSGA,
        isPosted,
        isPendingSubmission,
        isViewOnly,
        isInvoiceReviewLocked = false,
        isVault,
    } = props;
    const { control, errors, setValue } = useFormContext();
    const validationMode = useContext(ValidationModeContext);
    const [required, setRequired] = useState({ required: true });
    // ltoe.web.expenses.EnablePaymentClinicianDeductions
    const { ltoeWebExpensesEnablePaymentClinicianDeductions: isPaymentClinicianDeductionEnabled } =
        useFlags();

    useEffect(() => {
        if (mode === ExpenseModeEnum.CreditCard)
            return setRequired({
                required:
                    expense?.status === ExpenseStatusValuesEnum.Approved ||
                    expense?.status === ExpenseStatusValuesEnum.InvoiceReview,
            });

        return setRequired({
            required:
                (expense?.status !== ExpenseStatusValuesEnum.NonReimbursable &&
                    expense?.status !== ExpenseStatusValuesEnum.Abandoned) ||
                expense?.status === ExpenseStatusValuesEnum.Approved,
        });
    }, [expense?.status, expense?.type]);

    const getBillTypeOptions = () => {
        if (isClinicianReimbursement) {
            return billTypes.filter(
                (type) =>
                    type.value !== BillTypeEnum.ClinicianDeduction &&
                    type.value !== BillTypeEnum.SGA
            );
        }

        if (expenseClass === ExpenseClassEnum.Payment) {
            // Payment expenses can have ClinicianDeduction, but exclude SGA.
            // ClinicianDeduction is not available for Supplemental category, and is
            // hidden entirely while the EnablePaymentClinicianDeductions flag is off.
            return billTypes.filter(
                (type) =>
                    type.value !== BillTypeEnum.SGA &&
                    !(
                        type.value === BillTypeEnum.ClinicianDeduction &&
                        (!isPaymentClinicianDeductionEnabled ||
                            expenseCategory === ExpenseCategoryEnum.Supplemental)
                    )
            );
        }

        if (isSGA[index]) {
            return billTypes.filter(
                (type) =>
                    type.value === BillTypeEnum.NonBillTeam ||
                    type.value === BillTypeEnum.NonBillProducer ||
                    type.value === BillTypeEnum.SGA
            );
        }

        if (isVault) {
            return billTypes.filter(
                (type) =>
                    type.value === BillTypeEnum.Billable ||
                    type.value === BillTypeEnum.NonBillProducer
            );
        }

        if (expense?.expenseClass !== ExpenseClassEnum.IngestedCCTransaction) {
            return billTypes.filter((type) => type.value !== BillTypeEnum.SGA);
        }

        return billTypes;
    };

    return (
        <Grid2 size={{ xs: 12, md: 6 }}>
            <Controller
                control={control}
                name={`expenseEntries[${index}].billType`}
                defaultValue={!expense.billType ? '' : expense.billType}
                rules={{
                    required:
                        required.required &&
                        !(
                            mode === ExpenseModeEnum.Vault &&
                            validationMode === ValidationModeEnum.Save
                        )
                            ? 'Bill type is required'
                            : false,
                }}
                render={({ onChange, value }) => (
                    <ExpenseTextField
                        id="bill-type"
                        select
                        onChange={(e) => {
                            if (isSGA[index]) {
                                setValue(`expenseEntries[${index}].sgaBillType`, e.target.value);
                            } else {
                                setValue(
                                    `expenseEntries[${index}].bookingBillType`,
                                    e.target.value
                                );
                            }
                            onChange(e);
                        }}
                        value={!value ? '' : value}
                        label="Bill type"
                        {...required}
                        disabled={
                            isViewOnly ||
                            isInvoiceReviewLocked ||
                            (isPosted && isViewOnly) ||
                            (isPendingSubmission && mode !== ExpenseModeEnum.Payment)
                        }
                        error={Boolean(errors.expenseEntries?.[index]?.billType)}
                        helperText={errors.expenseEntries?.[index]?.billType?.message}
                    >
                        <MenuItem value="" disabled>
                            Select a bill type
                        </MenuItem>
                        {getBillTypeOptions().map((option, i) => (
                            <MenuItem
                                key={option.value}
                                value={option.value}
                                id={`bill-type-menu-option-${i}`}
                            >
                                {option.type}
                            </MenuItem>
                        ))}
                    </ExpenseTextField>
                )}
            />
        </Grid2>
    );
};

export default ExpenseBillType;
